import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Lock, Calendar, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const StaffLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: ""
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/auth/staff-login`, form);
      
      // Save staff token separately from main user token
      localStorage.setItem("staff_token", response.data.access_token);
      localStorage.setItem("staff_info", JSON.stringify(response.data.user));
      
      toast.success(`Benvenuto, ${response.data.user.name}!`);
      navigate("/staff-portal");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    }
    
    setLoading(false);
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4 sm:p-6" data-testid="staff-login-page">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-4 sm:pb-6">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Calendar className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
          </div>
          <CardTitle className="text-xl sm:text-2xl">Portale Staff</CardTitle>
          <CardDescription className="text-sm sm:text-base">
            Accedi con le credenziali fornite dal tuo responsabile
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="staff-login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm sm:text-base">Email</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  id="email"
                  type="email"
                  placeholder="staff@esempio.com"
                  className="pl-10 h-11 sm:h-10"
                  value={form.email}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  required
                  data-testid="staff-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm sm:text-base">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  id="password"
                  type="password"
                  placeholder="La tua password"
                  className="pl-10 h-11 sm:h-10"
                  value={form.password}
                  onChange={(e) => setForm({...form, password: e.target.value})}
                  required
                  data-testid="staff-password-input"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-11 sm:h-10 text-base sm:text-sm" 
              disabled={loading}
              data-testid="staff-login-button"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Accesso in corso...</span>
                </div>
              ) : (
                "Accedi"
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-xs sm:text-sm text-slate-500 space-y-1">
            <p>Non hai un account?</p>
            <p>Contatta il tuo responsabile per ottenere le credenziali di accesso.</p>
          </div>
          
          <div className="mt-4 flex justify-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/login")} 
              className="text-xs sm:text-sm gap-1"
              data-testid="admin-login-link"
            >
              <ArrowLeft className="h-3 w-3" />
              Sei un amministratore? Accedi qui
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffLogin;
